import React, { useLayoutEffect, useState } from 'react'
import { Loader, Button } from 'semantic-ui-react'

import { useTheme } from 'contexts/ThemeProvider'
import { Checkbox } from 'components/Common'
import themeStyles from './styles'

export const BUTTON_STATES = {
  primary: 'primary',
  secondary: 'secondary',
  cancel: 'cancel'
}

export const ModalContainer = ({ children, style }) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)

  return <div style={{ ...styles.container, ...style }}>{children}</div>
}

export const ConfirmModal = ({ hideModal, title, message, actions = [] }) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)

  return (
    <ModalContainer
      onClose={hideModal}
      style={{ ...styles.content, ...styles.modal }}
    >
      {title && (
        <div style={styles.header}>
          <p style={styles.title}>{title}</p>
        </div>
      )}
      {message && <p style={styles.message}>{message}</p>}
      {actions.length > 0 && (
        <div style={styles.actions}>
          {actions.map((action, i) => (
            <Button
              key={i}
              style={{ ...styles.button, ...action.style }}
              onClick={() => {
                action.onPress?.()
                if (!action.preventClose) {
                  hideModal()
                }
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </ModalContainer>
  )
}

export const LoadingModal = ({ message = 'Loading...', style }) => {
  const { theme, isDark } = useTheme()
  const styles = themeStyles(theme)

  return (
    <ModalContainer style={{ ...styles.content, ...style }}>
      <p style={styles.loadingText}>{message}</p>
      <Loader inverted={isDark} active inline='centered' />
    </ModalContainer>
  )
}

export const InfoModal = ({
  hideModal,
  title,
  message,
  showDismissLabel = true,
  dismissLabel = 'OK',
  renderIcon,
  style
}) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)

  return (
    <ModalContainer style={{ ...styles.content, ...styles.modal, ...style }}>
      {title && (
        <div style={styles.header}>
          <p style={styles.title}>{title}</p>
        </div>
      )}
      {renderIcon && renderIcon()}
      {message && <p style={styles.message}>{message}</p>}
      {showDismissLabel && (
        <div style={styles.button} onClick={hideModal}>
          <p style={styles.buttonText}>{dismissLabel}</p>
        </div>
      )}
    </ModalContainer>
  )
}

export const ErrorModal = ({
  hideModal,
  title,
  message,
  showDismissButton = true,
  dismissButtonStyle = {},
  dismissButtonOnClick = () => {},
  dismissLabel = 'OK',
  renderIcon,
  style
}) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)

  return (
    <ModalContainer style={{ ...styles.content, ...styles.modal, ...style }}>
      {renderIcon && renderIcon()}
      {title && (
        <div style={styles.header}>
          <p style={styles.errorTitle}>{title}</p>
        </div>
      )}
      {showDismissButton && <p style={styles.errorMessage}>{message}</p>}
      <Button
        style={{ ...styles.button, ...dismissButtonStyle }}
        onClick={() => {
          dismissButtonOnClick()
          hideModal()
        }}
      >
        {dismissLabel}
      </Button>
    </ModalContainer>
  )
}

const SESSION_ID = `s_${Date.now()}_${Math.random().toString(36).slice(2)}`

const readDismissed = scope => {
  try {
    const raw = window.localStorage.getItem(scope)
    if (!raw) {
      return false
    }
    const parsed = JSON.parse(raw)
    return parsed?.sessionId === SESSION_ID && parsed?.dismissed === true
  } catch {
    return false
  }
}

const writeDismissed = scope => {
  try {
    window.localStorage.setItem(
      scope,
      JSON.stringify({ sessionId: SESSION_ID, dismissed: true })
    )
  } catch {
    // ignore quota / privacy mode errors
  }
}

/**
 * Returns true if a RedirectModal with the given dismiss scope has been
 * suppressed via "Do not show again" during the current session.
 *
 * The dismiss flag is global across every RedirectModal instance that shares
 * the same `scope`, so ticking the checkbox once suppresses all modals in
 * the same scope for the rest of the session. The flag is reset on a full
 * page load (new SESSION_ID).
 */
export const isRedirectModalDismissed = scope => {
  return readDismissed(scope)
}

/**
 * @param {object} props
 * @param {() => void} props.hideModal
 * @param {string} [props.title]
 * @param {string} [props.message]
 * @param {string} [props.learnMoreUrl]
 * @param {string} [props.learnMoreLabel='Learn more.']
 * @param {string} [props.continueLabel='Continue']
 * @param {string} [props.cancelLabel='Cancel']
 * @param {string} [props.doNotShowLabel='Do not show again']
 * @param {() => void} [props.onContinue]
 * @param {string} [props.continueUrl]
 * @param {string} [props.dismissScope='redirectModal:dismissed'] Scope used to
 *   persist the "Do not show again" flag. All RedirectModal instances that
 *   share a scope will be suppressed together once any one of them is
 *   dismissed. The flag is session-only.
 * @param {boolean} [props.forceShow=false] Bypass the session-scoped dismiss
 *   flag and always render the modal.
 * @param {object} [props.style]
 */
export const RedirectModal = ({
  hideModal,
  title,
  message,
  learnMoreUrl,
  learnMoreLabel = 'Learn more.',
  continueLabel = 'Continue',
  cancelLabel = 'Cancel',
  doNotShowLabel = 'Do not show again',
  onContinue,
  continueUrl,
  dismissScope = 'redirectModal:dismissed',
  forceShow = false,
  style
}) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [suppressed] = useState(() => {
    return !forceShow && readDismissed(dismissScope)
  })

  useLayoutEffect(() => {
    if (suppressed) {
      hideModal()
    }
  }, [suppressed, hideModal])

  const handleContinue = () => {
    if (dontShowAgain) {
      writeDismissed(dismissScope)
    }
    onContinue?.()
    if (continueUrl) {
      window.open(continueUrl, '_blank', 'noopener,noreferrer')
    }
    hideModal()
  }

  const handleCancel = () => {
    hideModal()
  }

  const toggleDontShowAgain = () => {
    setDontShowAgain(prev => !prev)
  }

  if (suppressed) {
    return null
  }

  return (
    <ModalContainer style={{ ...styles.content, ...styles.modal, ...style }}>
      {title ? <p style={styles.redirectTitle}>{title}</p> : null}
      <p style={styles.messageBlock}>
        {message}
        {learnMoreUrl && (
          <>
            {' '}
            <a
              href={learnMoreUrl}
              target='_blank'
              rel='noopener noreferrer'
              style={styles.learnMore}
            >
              {learnMoreLabel}
            </a>
          </>
        )}
      </p>
      <div style={styles.redirectActions}>
        <button
          type='button'
          style={styles.continueButton}
          onClick={handleContinue}
          autoFocus
        >
          {continueLabel}
        </button>
        <button
          type='button'
          style={styles.cancelButton}
          onClick={handleCancel}
        >
          {cancelLabel}
        </button>
      </div>
      <div
        style={styles.doNotShowRow}
        onKeyDown={e => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault()
            toggleDontShowAgain()
          }
        }}
      >
        <Checkbox
          checked={dontShowAgain}
          onChange={e => {
            e.stopPropagation()
            toggleDontShowAgain()
          }}
        />
        <span
          style={styles.doNotShowLabel}
          onClick={e => {
            e.stopPropagation()
            toggleDontShowAgain()
          }}
        >
          {doNotShowLabel}
        </span>
      </div>
    </ModalContainer>
  )
}
